import type {
	Role,
	UserStatus,
	EventAllocation,
	CashExpenseCategory,
	EquityContribution,
	BankTransaction,
	Event,
	FinanceSummary,
	SalesOverview,
	SalesDetailPeriod,
	AttentionCounts,
	CashExpense,
	AppUser,
	User,
	LoyaltyStats,
	Contact,
	VoucherCode,
	EventEnquiry,
	EventEnquiryDetail,
	BlockedDate,
	BlockedDateStatus,
	Offering,
	PricingModel,
	DiscountReason,
	KegDrink,
	EventKeg,
	OfferingSetting,
	EnquiryStatus,
	EnquiryLostReason,
	EnquiryNoteKind,
	EnquiryChannel,
	LeadAttentionCounts,
	AlertStatus,
	EnquiryOwner,
	Attachment,
	EventStatus,
	Task,
	TaskArea,
	TaskAssignee,
	TaskDetail,
	TaskItem,
	TaskListResponse,
	TaskView,
	RecipeKind,
	DocumentSummary,
	DocumentDetail,
	DocumentProposal,
	RecipeCatalog,
	BatchCompletion,
} from '../types'
import { API_URL, request, requestText, requestUpload, type ImportResult } from './api/http'

export { API_URL, ApiError, errorMessage, isClientError, isForbidden, isUnauthorized } from './api/http'
export * from './api/invoices'
export * from './api/kegTaps'
export * from './api/quality'
export * from './api/schedule'
export * from './api/profile'
export * from './api/hiring'
export * from './api/navSettings'
export * from './api/pay'
export * from './api/timeClock'

interface IposImportResult extends ImportResult {
	warnings: string[]
}

export interface EnquiryPatch {
	offering?: Offering
	status?: EnquiryStatus
	owner_user_id?: string | null
	lost_reason?: EnquiryLostReason | null
	name?: string
	email?: string | null
	phone?: string | null
	contact_handle?: string | null
	guest_count?: number | null
	event_type?: string | null
	location?: string | null
	message?: string | null
	dates?: string[]
}

export const getMe = () => request<User>('/auth/me')

export const getEvents = () => request<Event[]>('/admin/events')

export const createEvent = (body: {
	event_date: string
	start_time?: string | null
	end_time?: string | null
	event_name: string
	event_type?: string
	offering?: string
	quoted_revenue_vnd?: number | null
	cash_revenue?: number
	currency?: string
	description?: string
}) => request<{ id: number }>('/admin/events', { method: 'POST', body: JSON.stringify(body) })

export const patchEvent = (
	id: number,
	body: {
		cash_revenue?: number | null
		transaction_count?: number | null
		description?: string | null
		event_name?: string
		event_date?: string
		event_type?: string | null
		start_time?: string | null
		end_time?: string | null
		status?: EventStatus
		guest_count?: number | null
		service_minutes?: number | null
		pricing_model?: PricingModel | null
		bar_tab_minimum?: number | null
		discount_reason?: DiscountReason | null
		vat_rate?: number | null
		is_price_gross?: boolean
		quoted_revenue_vnd?: number | null
	},
) => request<{ ok: true }>(`/admin/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) })

export const getSopFull = () => requestText('/training/full')
export const getSopEssentials = () => requestText('/training/essentials')
export const getGrowth = () => requestText('/training/growth')

export const listDocuments = () => request<DocumentSummary[]>('/documents')

export const getDocument = (slug: string) => request<DocumentDetail>(`/documents/${slug}`)

export const acknowledgeDocument = (slug: string, quizScore: number | null) =>
	request<{ ok: true }>(`/documents/${slug}/acknowledge`, {
		method: 'POST',
		body: JSON.stringify({ quiz_score: quizScore }),
	})

export const proposeDocumentChange = (slug: string, bodyMd: string, changeNote: string) =>
	request<{ ok: true }>(`/documents/${slug}/proposals`, {
		method: 'POST',
		body: JSON.stringify(changeNote ? { body_md: bodyMd, change_note: changeNote } : { body_md: bodyMd }),
	})

export const listDocumentProposals = () => request<DocumentProposal[]>('/documents/proposals')

export const acceptDocumentProposal = (id: number, rewardVnd: number | null, reviewNote: string) =>
	request<{ ok: true }>(`/documents/proposals/${id}/accept`, {
		method: 'POST',
		body: JSON.stringify(reviewNote ? { reward_vnd: rewardVnd, review_note: reviewNote } : { reward_vnd: rewardVnd }),
	})

export const rejectDocumentProposal = (id: number, reviewNote: string) =>
	request<{ ok: true }>(`/documents/proposals/${id}/reject`, {
		method: 'POST',
		body: JSON.stringify({ review_note: reviewNote }),
	})

export const listUsers = () => request<AppUser[]>('/admin/users')

export const listRecipes = () => request<RecipeCatalog>('/recipes')

interface CreatedUser extends AppUser {
	is_invitation_sent: boolean
}

export const createUser = (email: string, name: string, role: Role) =>
	request<CreatedUser>('/admin/users', {
		method: 'POST',
		body: JSON.stringify({ email, name, role }),
	})

export const updateUserRole = (email: string, role: Role) =>
	request<{ email: string; role: Role }>(`/admin/users/${encodeURIComponent(email)}/role`, {
		method: 'PATCH',
		body: JSON.stringify({ role }),
	})

export const updateUserStatus = (email: string, status: UserStatus) =>
	request<{ email: string; status: UserStatus }>(`/admin/users/${encodeURIComponent(email)}/status`, {
		method: 'PATCH',
		body: JSON.stringify({ status }),
	})

export const getEquityContributions = () => request<EquityContribution[]>('/admin/equity-contributions')

export const patchEquityContribution = (id: number, body: { category?: string; allocations?: EventAllocation[] }) =>
	request<{ ok: true }>(`/admin/equity-contributions/${id}`, {
		method: 'PATCH',
		body: JSON.stringify(body),
	})

export const importEquityContributions = (file: File) => requestUpload<ImportResult>('/admin/equity-contributions/import', file)

export const getBankTransactions = () => request<BankTransaction[]>('/admin/bank-transactions')

export const patchBankTransaction = (id: number, body: { category?: string; allocations?: EventAllocation[] }) =>
	request<{ ok: true }>(`/admin/bank-transactions/${id}`, {
		method: 'PATCH',
		body: JSON.stringify(body),
	})

export const importBankTransactions = (file: File) => requestUpload<ImportResult>('/admin/bank-transactions/import', file)

export const getFinanceSummary = () => request<FinanceSummary>('/admin/finance/summary')

export const getSalesOverview = (period: SalesDetailPeriod) => request<SalesOverview>(`/admin/finance/sales?period=${period}`)

export const getAttentionCounts = () => request<AttentionCounts>('/admin/finance/attention-counts')

export const importIposSales = (file: File) => requestUpload<IposImportResult>('/admin/ipos-sales/import', file)

export const getCashExpenses = () => request<CashExpense[]>('/admin/cash-expenses')

export const createCashExpense = (body: {
	date: string
	amount: number
	currency?: string
	category: CashExpenseCategory
	description?: string
}) => request<{ id: number }>('/admin/cash-expenses', { method: 'POST', body: JSON.stringify(body) })

export const patchCashExpense = (id: number, body: { category?: string; allocations?: EventAllocation[]; date?: string }) =>
	request<{ ok: true }>(`/admin/cash-expenses/${id}`, {
		method: 'PATCH',
		body: JSON.stringify(body),
	})

export const importCashExpenses = (file: File) => requestUpload<ImportResult>('/admin/cash-expenses/import', file)

export const getLoyaltyStats = () => request<LoyaltyStats>('/admin/loyalty/stats')

export const importVoucherCodes = (file: File) => requestUpload<ImportResult>('/admin/loyalty/codes/import', file)

export const setRegistrationProgram = (programId: number) =>
	request<{ ok: true }>(`/admin/loyalty/programs/${programId}/registration`, { method: 'PATCH' })

export const updateProgram = (
	programId: number,
	body: {
		slug?: string | null
		is_active?: boolean
		pass_title?: string | null
		pass_subtitle?: string | null
		pass_description?: string | null
		pass_how_it_works?: string | null
		claim_title?: string | null
		claim_subtitle?: string | null
	},
) => request<{ ok: true }>(`/admin/loyalty/programs/${programId}`, { method: 'PATCH', body: JSON.stringify(body) })

export const getContacts = (search?: string) =>
	request<Contact[]>(`/admin/loyalty/contacts${search ? `?q=${encodeURIComponent(search)}` : ''}`)

export const updateContactNotes = (phone: string, notes: string | null) =>
	request<{ ok: true }>(`/admin/loyalty/contacts/${encodeURIComponent(phone)}`, {
		method: 'PATCH',
		body: JSON.stringify({ notes }),
	})

export const getProgramCodes = (programId: number) => request<VoucherCode[]>(`/admin/loyalty/programs/${programId}/codes`)

export const getEnquiries = () => request<EventEnquiry[]>('/admin/enquiries')

export const createEnquiry = (body: {
	offering: Offering
	channel: EnquiryChannel
	name: string
	email?: string
	phone?: string
	contact_handle?: string
	event_date?: string
	guest_count?: number
	event_type?: string
	location?: string
	message?: string
}) => request<{ id: number }>('/admin/enquiries', { method: 'POST', body: JSON.stringify(body) })

export const getEnquiry = (id: number) => request<EventEnquiryDetail>(`/admin/enquiries/${id}`)

export const patchEnquiry = (id: number, body: EnquiryPatch) =>
	request<{ ok: true }>(`/admin/enquiries/${id}`, { method: 'PATCH', body: JSON.stringify(body) })

export const addEnquiryNote = (id: number, body: string, kind: EnquiryNoteKind = 'note') =>
	request<{ id: number }>(`/admin/enquiries/${id}/notes`, {
		method: 'POST',
		body: JSON.stringify({ body, kind }),
	})

export const mergeEnquiry = (id: number, into: number) =>
	request<{ ok: true; into: number }>(`/admin/enquiries/${id}/merge`, {
		method: 'POST',
		body: JSON.stringify({ into }),
	})

export const getLeadAttentionCounts = () => request<LeadAttentionCounts>('/admin/enquiries/attention-counts')

export const getAlertStatus = () => request<AlertStatus>('/admin/enquiries/alert-status')

export const getLeadOwners = () => request<EnquiryOwner[]>('/admin/enquiries/owners')

export const getBlockedDates = () => request<BlockedDate[]>('/admin/enquiries/blocked-dates')

export const upsertBlockedDate = (body: { date: string; offering: Offering | 'both'; status: BlockedDateStatus; note?: string }) =>
	request<{ ok: true }>('/admin/enquiries/blocked-dates', { method: 'POST', body: JSON.stringify(body) })

export const deleteBlockedDate = (date: string, offering: Offering | 'both') =>
	request<{ ok: true }>(`/admin/enquiries/blocked-dates/${date}/${offering}`, { method: 'DELETE' })

export const getDueTasks = () => request<TaskItem[]>('/tasks/due')

export const getOpenKegTasks = () => request<TaskItem[]>('/tasks/kegs')

export const getEventTasks = (eventId: number) => request<Task[]>(`/tasks/events/${eventId}`)

export const getEnquiryTasks = (enquiryId: number) => request<Task[]>(`/tasks/enquiries/${enquiryId}`)

export const getTasks = (view: TaskView) => request<TaskListResponse>(`/tasks?view=${view}`)

export const getTask = (id: number) => request<TaskDetail>(`/tasks/${id}`)

export const getTaskAssignees = (area: TaskArea, recipeKind?: RecipeKind) =>
	request<TaskAssignee[]>(`/tasks/assignees?area=${area}${recipeKind ? `&recipe_kind=${recipeKind}` : ''}`)

export const createBatchOrder = (body: {
	recipe_id: number
	planned_volume_ml: number
	count: number
	due_date: string
	owner_user_id: string
	description?: string | null
}) => request<{ task_ids: number[] }>('/tasks/batches', { method: 'POST', body: JSON.stringify(body) })

export const completeBatch = (id: number, body: BatchCompletion) =>
	request<{ id: number }>(`/tasks/${id}/batch-completion`, { method: 'POST', body: JSON.stringify(body) })

export const switchBatchToCurrentRecipe = (id: number) =>
	request<{ id: number; recipe_version_id: number }>(`/tasks/${id}/batch-version`, { method: 'POST' })

export const getKegDrinks = () => request<KegDrink[]>('/admin/event-prep/drinks')

export const getEventKegs = (eventId: number) => request<EventKeg[]>(`/admin/event-prep/events/${eventId}/kegs`)

export const setEventKegs = (eventId: number, drinkId: number, kegs: number) =>
	request<{ ok: true }>(`/admin/event-prep/events/${eventId}/kegs/${drinkId}`, { method: 'PUT', body: JSON.stringify({ kegs }) })

export const getOfferingSettings = () => request<OfferingSetting[]>('/admin/event-prep/offerings')

export const getMinimumSuggestion = (date: string, offering: Offering) =>
	request<{ suggested: number | null; basis: 'ipos' | 'setting'; days: number }>(
		`/admin/events/minimum-suggestion?date=${date}&offering=${offering}`,
	)

export const createTask = (body: {
	title: string
	description?: string | null
	area?: TaskArea
	due_date?: string | null
	is_important?: boolean
	owner_user_id?: string | null
	enquiry_id?: number | null
	event_id?: number | null
}) => request<{ id: number }>('/tasks', { method: 'POST', body: JSON.stringify(body) })

export interface TaskPatch {
	title?: string
	description?: string | null
	is_done?: boolean
	is_important?: boolean
	due_date?: string | null
	owner_user_id?: string | null
	result_note?: string | null
}

export const patchTask = (id: number, body: TaskPatch) =>
	request<{ id: number }>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(body) })

export const deleteTask = (id: number) => request<{ id: number }>(`/tasks/${id}`, { method: 'DELETE' })

export type AttachmentTarget = { enquiry_id: number } | { event_id: number } | { task_id: number }

function targetQuery(target: AttachmentTarget): string {
	const [key, value] = Object.entries(target)[0]
	return `${key}=${value}`
}

export const getAttachments = (target: AttachmentTarget) => request<Attachment[]>(`/admin/attachments?${targetQuery(target)}`)

export const uploadAttachment = (target: AttachmentTarget, file: File) =>
	requestUpload<{ id: number; key: string }>(`/admin/attachments?${targetQuery(target)}`, file)

export const deleteAttachment = (id: number) => request<{ id: number }>(`/admin/attachments/${id}`, { method: 'DELETE' })

export const attachmentUrl = (id: number) => `${API_URL}/admin/attachments/${id}/download`
